/* Ergo Dream — product page: gallery, lightbox, variant picker, floating Add to Cart.
   Variant data comes from Shopify's product JSON; prices, inventory and labels are
   re-rendered by Shopify (Section Rendering API), never calculated here. */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const strings = (window.theme && window.theme.strings) || {};

  /* ------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------ */
  class Gallery {
    constructor(root) {
      this.root = root;
      this.track = $('[data-gallery-track]', root);
      this.slides = $$('[data-gallery-slide]', root);
      this.thumbs = $$('[data-gallery-thumb]', root);
      this.current = $('[data-gallery-current]', root);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (!this.track || this.slides.length === 0) return;

      $('[data-gallery-prev]', root)?.addEventListener('click', () => this.go(this.index - 1));
      $('[data-gallery-next]', root)?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.go(Number(thumb.dataset.galleryThumb)))
      );

      // Keyboard arrows when focus is inside the gallery
      root.addEventListener('keydown', (event) => {
        if (event.target.matches('input, textarea, select')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      });

      // Mobile: follow the native swipe position
      let scrollTimer;
      this.track.addEventListener('scroll', () => {
        if (desktop.matches) return;
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const width = this.track.clientWidth || 1;
          const i = Math.round(this.track.scrollLeft / width);
          if (i !== this.index) this.setActive(i, false);
        }, 60);
      }, { passive: true });

      desktop.addEventListener('change', () => this.go(this.index, false));
      this.initZoom();
      this.initLightbox();
      if (!desktop.matches && this.index > 0) this.scrollTo(this.index, false);
    }

    go(i, smooth = true) {
      const count = this.slides.length;
      const next = ((i % count) + count) % count;
      this.setActive(next, true);
      if (!desktop.matches) this.scrollTo(next, smooth);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.go(i);
    }

    scrollTo(i, smooth = true) {
      const left = this.slides[i].offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({ left, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
    }

    setActive(i, moveThumb = true) {
      this.index = i;
      this.slides.forEach((slide, n) => {
        const active = n === i;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
        const opener = $('[data-lightbox-open]', slide);
        if (opener) opener.tabIndex = active ? 0 : -1;
        if (!active) slide.classList.remove('is-zoomed');
      });
      this.thumbs.forEach((thumb, n) => {
        if (n === i) {
          thumb.setAttribute('aria-current', 'true');
          if (moveThumb) {
            const list = thumb.closest('.gallery__thumbs');
            if (list && list.scrollWidth > list.clientWidth) {
              list.scrollTo({ left: thumb.offsetLeft - list.clientWidth / 2 + thumb.clientWidth / 2, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
            }
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.current) this.current.textContent = String(i + 1);
    }

    initZoom() {
      if (!this.root.classList.contains('gallery--zoom')) return;
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.slides.forEach((slide) => {
        const frame = $('[data-zoom-frame]', slide);
        const img = frame && $('img', frame);
        if (!img) return;
        slide.addEventListener('pointermove', (event) => {
          if (!fine.matches || !desktop.matches || reducedMotion.matches) return;
          const rect = frame.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${x}% ${y}%`;
          slide.classList.add('is-zoomed');
        });
        slide.addEventListener('pointerleave', () => slide.classList.remove('is-zoomed'));
      });
    }

    initLightbox() {
      const section = this.root.closest('[data-product-root]') || document;
      const dialog = $('[data-lightbox]', section);
      if (!dialog) return;
      const items = $$('[data-lightbox-item]', dialog);
      let opener = null;

      const open = (i) => {
        opener = document.activeElement;
        dialog.showModal();
        document.documentElement.classList.add('scroll-locked');
        requestAnimationFrame(() => {
          dialog.classList.add('is-open');
          const target = items.find((item) => Number(item.dataset.lightboxItem) === i) || items[0];
          target?.scrollIntoView({ block: 'start' });
        });
      };
      const close = () => {
        dialog.classList.remove('is-open');
        dialog.close();
        document.documentElement.classList.remove('scroll-locked');
        opener?.focus({ preventScroll: true });
      };

      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lightbox-open]');
        if (!trigger) return;
        const value = trigger.dataset.lightboxOpen;
        open(value === 'current' ? this.index : Number(value));
      });
      $('[data-lightbox-close]', dialog)?.addEventListener('click', close);
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
      dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    }
  }

  /* ------------------------------------------------------------------
     Variant picker
     ------------------------------------------------------------------ */
  class ProductSection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productUrl = root.dataset.productUrl;
      const galleryEl = $('[data-gallery]', root);
      this.gallery = galleryEl ? new Gallery(galleryEl) : null;
      this.picker = $('[data-variant-picker]', root);
      if (this.picker) {
        const json = $('[data-variants-json]', this.picker);
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.picker.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-input]')) this.onOptionChange();
        });
        this.markAvailability();
      }
      this.initSticky();
    }

    selectedOptions() {
      return $$('.variant-option', this.picker).map((fieldset) => {
        const select = $('select[data-option-input]', fieldset);
        if (select) return select.value;
        const checked = $('input[data-option-input]:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      $$('.variant-option', this.picker).forEach((fieldset, i) => {
        const label = $('[data-option-selected]', fieldset);
        if (label) label.textContent = options[i] || '';
      });
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.markAvailability();
      this.applyVariant(variant);
    }

    markAvailability() {
      const options = this.selectedOptions();
      $$('.variant-option', this.picker).forEach((fieldset, index) => {
        $$('[data-option-input]', fieldset).forEach((input) => {
          const values = input.tagName === 'SELECT' ? $$('option', input) : [input];
          values.forEach((el) => {
            const candidate = options.slice();
            candidate[index] = el.value;
            const available = this.variants.some(
              (v) => v.available && v.options.every((value, i) => value === candidate[i])
            );
            el.classList.toggle('is-unavailable', !available);
            if (el.tagName === 'INPUT') el.nextElementSibling?.classList.toggle('is-unavailable', !available);
          });
        });
      });
    }

    setButtons(variant) {
      $$('[data-add-button]', this.root).forEach((button) => {
        const label = $('[data-add-label]', button);
        const available = Boolean(variant && variant.available);
        button.disabled = !available;
        if (available) button.removeAttribute('aria-disabled');
        if (label) label.textContent = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      });
    }

    async applyVariant(variant) {
      $$('[data-variant-input]', this.root).forEach((input) => {
        input.value = variant ? variant.id : '';
        input.disabled = !variant;
      });
      this.setButtons(variant);
      if (!variant) return;

      if (variant.featured_media && this.gallery) this.gallery.goToMedia(variant.featured_media.id);

      if (this.root.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Let Shopify render prices, inventory and labels for this variant.
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(`${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        $$('[data-update-id]', this.root).forEach((el) => {
          const next = $(`[data-update-id="${el.dataset.updateId}"]`, doc);
          if (!next) return;
          if (el.dataset.updateId === 'quantity') {
            // Keep the customer's chosen quantity; only refresh the limits.
            const input = $('input', el);
            const nextInput = $('input', next);
            if (input && nextInput) {
              ['min', 'max', 'step'].forEach((attr) => {
                if (nextInput.hasAttribute(attr)) input.setAttribute(attr, nextInput.getAttribute(attr));
                else input.removeAttribute(attr);
              });
              if (input.max && Number(input.value) > Number(input.max)) input.value = input.max;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
          el.innerHTML = next.innerHTML;
        });
        this.setButtons(variant);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }

    /* ---------------- Floating Add to Cart ---------------- */
    initSticky() {
      const bar = $('[data-sticky-atc]', this.root);
      const buy = $('[data-buy-buttons]', this.root);
      if (!bar || !buy || !('IntersectionObserver' in window)) return;
      const footer = $('.site-footer');
      let buyAbove = false;
      let footerVisible = false;

      const update = () => {
        const show = buyAbove && !footerVisible;
        bar.classList.toggle('is-visible', show);
        bar.toggleAttribute('inert', !show);
        bar.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('has-sticky-atc', show);
      };

      // The root is extended far below the viewport, so the buy buttons only stop
      // "intersecting" once they have scrolled above the top edge. This also works
      // when the page jumps (anchor links, restored scroll position).
      new IntersectionObserver(
        ([entry]) => {
          buyAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          update();
        },
        { rootMargin: '0px 0px 100000px 0px' }
      ).observe(buy);

      if (footer) {
        // Only step aside once the footer fills the lower third of the screen,
        // so short product pages still get the floating bar.
        new IntersectionObserver(
          ([entry]) => {
            footerVisible = entry.isIntersecting;
            update();
          },
          { rootMargin: '0px 0px -35% 0px' }
        ).observe(footer);
      }

      // If a drawer opens, get out of the way.
      document.addEventListener('cart:updated', update);
    }
  }

  const init = (scope = document) => {
    $$('[data-product-root]', scope).forEach((root) => {
      if (root._product) return;
      root._product = new ProductSection(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
